import Feature from 'ol/Feature';
import {Point} from 'ol/geom';
import {Fill, RegularShape, Stroke, Style} from 'ol/style';
import {hexToRGBA} from "@/assets/js/utils";
import {fire as fireEffect} from './fire';

/**
 * Shared explosion effect — a flash burst (an expanding, fading multi-point
 * star) mixed with short-lived fire icons that flicker at the impact point.
 *
 * This is the single source of truth for "something just blew up here" and is
 * reused by the war animations (missiles, airstrike, drone) so every impact
 * looks consistent. All coordinates are EPSG:3857 (metres); `fireSpread` is in
 * the same projection units, while flash radii are screen pixels.
 */

const FLASH_TICK = 40;

function flashBurst(layer, coordinate, {color, flashColor, points, maxRadius, step}) {
    const burst = new Feature(new Point(coordinate));
    burst.set('selectable', false);

    const burstStyle = (progress) => {
        const radius = 4 + progress * maxRadius;
        const alpha = 1 - progress;
        return new Style({
            image: new RegularShape({
                points,
                radius,
                radius2: radius / 2.5,
                fill: new Fill({color: hexToRGBA(flashColor, alpha)}),
                stroke: new Stroke({color: hexToRGBA(color, alpha), width: 1})
            })
        });
    };

    let progress = 0;
    burst.setStyle(burstStyle(progress));
    layer.getSource().addFeature(burst);

    const interval = setInterval(() => {
        progress += step;
        if (progress >= 1) {
            clearInterval(interval);
            layer.getSource().removeFeature(burst);
            return;
        }
        burst.setStyle(burstStyle(progress));
    }, FLASH_TICK);
}

/**
 * Trigger one explosion (flash + optional fire) at an EPSG:3857 `coordinate`.
 *
 * @param layer        ol layer to draw on
 * @param coordinate   [x, y] in EPSG:3857
 * @param options.color        accent / stroke colour (usually news.color)
 * @param options.flashColor   core flash colour (default fiery orange)
 * @param options.points       star points of the flash
 * @param options.maxRadius    how big the flash grows, in px
 * @param options.step         flash growth per frame (smaller = slower)
 * @param options.fire         whether to leave lingering fire (default true)
 * @param options.fireCount    number of fire icons
 * @param options.fireSpread   scatter radius for fire, in EPSG:3857 metres
 * @param options.fireDuration how long fire burns before fading, in ms
 */
export function explosion(layer, coordinate, options = {}) {
    const {
        color = '#ff5722',
        flashColor = '#ffb347',
        points = 9,
        maxRadius = 14,
        step = 0.06,
        fire = true,
        fireCount = 4,
        fireSpread = 600,
        fireDuration = 2500
    } = options;

    flashBurst(layer, coordinate, {color, flashColor, points, maxRadius, step});
    if (fire) {
        fireEffect(layer, {
            spawn: () => [
                coordinate[0] + (Math.random() - 0.5) * fireSpread,
                coordinate[1] + (Math.random() - 0.5) * fireSpread
            ],
            count: fireCount,
            duration: fireDuration
        });
    }
}
